/**
 * The ids of the purpose-built compliance lists (PD-16252), as constants.
 *
 * WHY. Each Needs Attention list is opened by a Compliance dashboard card
 * through a URL carrying the list id. When both sides spelled the id as a
 * literal, a card could point at a list that did not exist — the Adverse Action
 * card did exactly that while its list was still a placeholder. Naming the id
 * once means the card and the list cannot disagree at all, rather than
 * disagreeing until a test catches it.
 *
 * These strings are also part of the URL contract: a bookmarked or shared
 * Needs Attention link carries one, so changing a value breaks saved links and
 * any saved per-list layout keyed on it. `attention-lists.test.ts` pins them.
 */
export const COMPLIANCE_ATTENTION_LIST_IDS = {
	controlledNoDea: 'compliance-controlled-no-dea',
	deadLicense: 'compliance-dead-license',
	adverseAction: 'compliance-adverse-action',
	// PD-16615: the Adverse Action card's other two views — its flag-only
	// group, and the items a review is hiding.
	adverseFlagOnly: 'compliance-adverse-flag-only',
	adverseReviewed: 'compliance-adverse-reviewed',
	wrongNumber: 'compliance-wrong-number',
	failedLookup: 'compliance-failed-lookup',
	// PD-17113: a confirmed DEA registered at another location than the
	// account's.
	deaOtherAddress: 'compliance-dea-other-address',
	expiringSoon: 'compliance-expiring-soon',
	noVerifiedLicense: 'compliance-no-verified-license',
	linkEnded: 'compliance-link-ended',
	readyToConfirm: 'compliance-ready-to-confirm',
	replacementAvailable: 'compliance-replacement-available',
	recentlyFixed: 'compliance-recently-fixed',
	// PD-16652: the "Check Your Records" cards (epic PD-16647) — the same
	// license twice on one account, and one license on several accounts.
	duplicateLicense: 'compliance-duplicate-license',
	sharedLicense: 'compliance-shared-license',
	// PD-16653: each card's Reviewed view — the groups a decision is hiding.
	duplicateLicenseReviewed: 'compliance-duplicate-license-reviewed',
	sharedLicenseReviewed: 'compliance-shared-license-reviewed',
} as const;

export type ComplianceAttentionListId =
	(typeof COMPLIANCE_ATTENTION_LIST_IDS)[keyof typeof COMPLIANCE_ATTENTION_LIST_IDS];
